import { Box, Button, Popover } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type { CommitIntent } from './InlineTextCell';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import {
  Calendar,
  addDaysIso,
  addMonthsIso,
  endOfMonthIso,
  startOfMonthIso,
} from '@/components/ui';
import { todayIso } from '@/lib';

/**
 * Click-to-edit *date* cell: the same inline pattern as `InlineTextCell`, but
 * the editor is a native `<input type="date">` rather than free text.
 *
 * The interaction copies the webapp's inline date field
 * (`Web/src/forms/components/fields/DateInputField.tsx`, `mode="inline"`):
 * no opening icon, and a click anywhere in the field opens the calendar rather
 * than only a click on an indicator.
 *
 * The field is a native `<input type="date">`; the *panel* is ours
 * (`components/ui/Calendar`). Splitting it that way is the whole design: the
 * native element gives locale-ordered segments and the guarantee that no
 * invalid date can be typed, while its panel is browser chrome that cannot be
 * styled at all — it arrived in Chrome's greys and square corners beside our
 * rounded, tokenised surfaces, which is what the feedback was about.
 * `showPicker()` is therefore never called; a click opens the popover instead.
 *
 * Three deliberate departures from the webapp's component:
 *
 * 1. Not `@mui/x-date-pickers-pro`: that is MUI X Pro (licensed) and no
 *    dependency here, and even the free tier needs a date adapter that would
 *    reintroduce local-time `Date` parsing (`lib/epochDay.ts`). The panel's
 *    arithmetic is `ui/calendarModel.ts`, in UTC.
 * 2. The field is NOT read-only, where the webapp's is — the segments stay
 *    typeable, which costs nothing since they cannot hold an invalid date, and
 *    a keyboard user never has to reach for the calendar at all.
 * 3. The panel opens on mount only when editing began with a CLICK, where the
 *    webapp's inline field is always open. A Tab into the field must leave it
 *    closed so the editor's field walk (title -> hours -> target,
 *    `useFieldEditing`) still works.
 * 4. While the panel is open it owns the arrow keys and draws the walk itself
 *    (`Calendar`'s `focusedDate`); the panel never takes DOM focus, so there
 *    was otherwise no keyboard in it at all. Escape closes the panel before it
 *    cancels the edit, which is also how the native segment stepping is got
 *    back.
 *
 * The value in and out is ISO `YYYY-MM-DD` — exactly what the element wants,
 * so no local-time `Date` is ever constructed here (see `lib/epochDay.ts`).
 */

/** The keys the open panel takes over from the native segments. */
const PANEL_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'Enter',
]);

/** One keyboard step through the grid, from `iso`. Pure. */
const stepDay = (iso: string, key: string): string => {
  switch (key) {
    case 'ArrowLeft':
      return addDaysIso(iso, -1);
    case 'ArrowRight':
      return addDaysIso(iso, 1);
    case 'ArrowUp':
      return addDaysIso(iso, -7);
    case 'ArrowDown':
      return addDaysIso(iso, 7);
    case 'PageUp':
      return addMonthsIso(iso, -1);
    case 'PageDown':
      return addMonthsIso(iso, 1);
    case 'Home':
      return startOfMonthIso(iso);
    case 'End':
      return endOfMonthIso(iso);
    default:
      return iso;
  }
};

export type InlineDateCellProps = {
  /** ISO `YYYY-MM-DD`, or empty for no date. */
  value: string;
  display: ReactNode;
  editing: boolean;
  onStartEdit: () => void;
  onCommit: (value: string, intent: CommitIntent) => void;
  onCancel: () => void;
  align?: 'left' | 'right';
  underlineColor?: string;
  inputWidth?: number | string;
  displaySx?: SxProps<Theme>;
  automationId?: string;
  /**
   * True when editing was entered by a click on this cell, so the calendar
   * opens with the field. Comes from the editor's `editing.openPicker` — a ref
   * cannot carry it, because the cell remounts when editing starts.
   */
  autoOpen?: boolean;
  /**
   * An extra action under the calendar grid — today, the plan list's "Set dates
   * in the timeline" link.
   *
   * A slot rather than a hard-coded link, so this cell keeps knowing only about
   * dates: what the action MEANS belongs to the caller, while the ordering that
   * makes it safe (commit first, then act) belongs here, next to `commit`.
   */
  panelAction?: {
    label: string;
    /** Material Symbol name, rendered before the label. */
    icon: string;
    onSelect: () => void;
    automationId?: string;
  };
};

type DateEditorProps = Pick<
  InlineDateCellProps,
  | 'value'
  | 'onCommit'
  | 'onCancel'
  | 'align'
  | 'underlineColor'
  | 'inputWidth'
  | 'automationId'
  | 'autoOpen'
  | 'panelAction'
>;

/** Mounted fresh each time editing starts, so the buffer seeds at mount. */
const DateEditor = ({
  value,
  onCommit,
  onCancel,
  align,
  underlineColor,
  inputWidth,
  automationId,
  autoOpen = false,
  panelAction,
}: DateEditorProps) => {
  const [buffer, setBuffer] = useState(value);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => startOfMonthIso(value || todayIso()));
  /** Where the panel's keyboard walk is; empty until an arrow key starts it. */
  const [focusedDate, setFocusedDate] = useState('');
  /*
    The element is held twice: a ref for the imperative focus in the mount
    effect, and state because the popover's `anchorEl` is read during render —
    and a ref may not be (the same split `PlanRow` makes for its own popover).
  */
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);
  const holdInput = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    setAnchorEl(element);
  };
  const committedRef = useRef(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      // Only a click asks for the calendar up front; a Tab into the field must
      // leave it closed, or the panel is in the way of the field walk
      // (departure 3 above).
      if (autoOpen) setOpen(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [autoOpen]);

  /**
   * What the field currently holds.
   *
   * An empty `value` is ambiguous on a date input: it means *either* nothing
   * typed at all *or* a date only half-typed (`2026-__-05`). `validity.badInput`
   * separates them — true only for the incomplete case. A half-typed date is an
   * abandoned edit, so it keeps the original; a genuinely empty field clears
   * the date, which `parseDateInput('')` then applies as `undefined`.
   */
  const currentValue = (): string => {
    const input = inputRef.current;
    if (!input) return buffer;
    if (input.value !== '') return input.value;
    return input.validity.badInput ? value : '';
  };

  const commit = (intent: CommitIntent = 'commit', next?: string) => {
    if (committedRef.current) return;
    committedRef.current = true;
    onCommit(next ?? currentValue(), intent);
  };

  return (
    <>
      <Box
        component="input"
        type="date"
        ref={holdInput}
        data-automation-id={automationId ? `${automationId}-input` : undefined}
        /*
          UNCONTROLLED, deliberately. React's `onChange` on an input is the
          `input` event, which fires on every segment keystroke — and a date
          input whose segments are half-filled reports `value === ''`. Feeding
          that back through a controlled `value` wiped the segments the moment
          you typed the first digit: the "only one number allowed, then it
          re-selects" bug. The element owns its own text; `buffer` only tracks
          the last COMPLETE value, for the panel and the month.
        */
        defaultValue={value}
        onClick={(event) => {
          event.stopPropagation();
          // The whole field is the affordance, not a calendar button: the
          // native indicator is hidden below, so a click anywhere opens our
          // panel (the webapp's inline field behaves the same way). While it is
          // already open this click lands on the popover's own backdrop, which
          // closes it — so the field toggles without tracking that here.
          setOpen(true);
        }}
        onChange={(event) => {
          /*
            Buffer only — never a commit.

            A date input fires `change` as soon as the three segments make a
            valid date, which on a field that ALREADY has one is after a single
            keystroke: typing the month over `2026-07-03` produced a complete
            date, committed, and unmounted the editor mid-edit. That is the
            "only one number allowed, then everything re-selects" bug.
            Committing belongs to Enter, blur and a day click, exactly as in
            `InlineTextCell`.
          */
          const next = (event.target as HTMLInputElement).value;
          if (next === '') return;
          setBuffer(next);
          setMonth(startOfMonthIso(next));
        }}
        onBlur={() => commit()}
        onKeyDown={(event) => {
          /*
            While the panel is open it owns the arrows, and the walk is drawn
            by `focusedDate` — the panel never takes DOM focus, so without this
            it had no keyboard at all. The cost is the native segment stepping
            that ArrowUp/Down would otherwise do; that stays available with the
            panel closed (a Tab into the field leaves it closed by design), and
            Escape closes the panel before it cancels the edit, so it is one
            keystroke away.
          */
          if (open && PANEL_KEYS.has(event.key)) {
            event.preventDefault();
            const from = focusedDate || currentValue() || todayIso();
            if (event.key === 'Enter') {
              setBuffer(from);
              if (inputRef.current) inputRef.current.value = from;
              commit('commit', from);
              return;
            }
            const next = stepDay(from, event.key);
            setFocusedDate(next);
            setMonth(startOfMonthIso(next));
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            const mod = event.metaKey || event.ctrlKey;
            if (mod && event.shiftKey) commit('new-child');
            else if (mod && event.altKey) commit('new-parent-sibling');
            else if (mod) commit('new-sibling');
            else commit('commit');
          } else if (event.key === 'Tab') {
            event.preventDefault();
            commit(event.shiftKey ? 'tab-prev' : 'tab-next');
          } else if (event.key === 'Escape') {
            event.preventDefault();
            // The panel first: closing it is not abandoning the edit.
            if (open) {
              setOpen(false);
              setFocusedDate('');
              return;
            }
            committedRef.current = true;
            onCancel();
          }
        }}
        sx={{
          fontFamily: 'var(--font-family)',
          fontSize: 14,
          color: 'var(--fg-1)',
          background: 'transparent',
          border: 'none',
          borderBottom: `var(--border-width-thick) solid ${underlineColor}`,
          outline: 'none',
          p: '0 1px',
          width: inputWidth,
          minWidth: 0,
          textAlign: align,
          // Clicking the field is what opens the calendar, so the row does not
          // spend width on an icon — the webapp's inline mode drops it too.
          cursor: 'pointer',
          '&::-webkit-calendar-picker-indicator': { display: 'none' },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false);
          setFocusedDate('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: align === 'right' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: align === 'right' ? 'right' : 'left' }}
        /*
          The field keeps focus the whole time the panel is open: it owns the
          keyboard (segments, Tab out, Escape) and its blur is what commits.
          Without `disableAutoFocus` the popover would steal focus on open and
          commit the edit immediately, closing itself.
        */
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        slotProps={{
          paper: {
            /*
              And a mousedown inside the panel must not move focus either —
              blur would commit and unmount the editor before the day's click
              ever fired. preventDefault on mousedown keeps the caret in the
              field; the click still lands.
            */
            onMouseDown: (event) => event.preventDefault(),
            // The same surface as the row's other popover (`ResourcesPopover`),
            // so the picker reads as one of the family rather than a stray card.
            sx: {
              mt: 0.5,
              border: 'var(--border-width-thin) solid var(--border-subtle)',
              borderRadius: 'var(--border-radius-medium)',
              boxShadow: 'var(--shadow-small)',
            },
          },
        }}
        data-automation-id={automationId ? `${automationId}-popover` : undefined}
      >
        <Calendar
          value={buffer}
          month={month}
          focusedDate={focusedDate}
          onMonthChange={setMonth}
          onSelect={(iso) => {
            setBuffer(iso);
            if (inputRef.current) inputRef.current.value = iso;
            commit('commit', iso);
          }}
          automationId={automationId ? `${automationId}-calendar` : undefined}
        />
        {panelAction ? (
          /*
            Sits inside the paper, so it inherits the paper's `onMouseDown`
            preventDefault — without which pressing it would blur the field,
            blur would commit, and the editor would unmount before the click
            ever fired (the same hazard the day cells have; ADR 0029).

            `commit()` with no argument takes `currentValue()`, so the rule is
            the one blur already follows: a complete date commits, a half-typed
            one leaves the original alone, an emptied field clears. Committing
            BEFORE the action matters — the action may unmount this editor.
          */
          <Box
            sx={{
              borderTop: 'var(--border-width-thin) solid var(--border-divider)',
              px: 0.5,
              pt: 0.5,
            }}
          >
            <Button
              size="tiny"
              variant="text"
              fullWidth
              startIcon={<MaterialSymbol name={panelAction.icon} size={16} />}
              onClick={() => {
                commit();
                panelAction.onSelect();
              }}
              data-automation-id={panelAction.automationId}
              sx={{ justifyContent: 'flex-start', color: 'var(--color-blue-100)' }}
            >
              {panelAction.label}
            </Button>
          </Box>
        ) : null}
      </Popover>
    </>
  );
};

export const InlineDateCell = ({
  value,
  display,
  editing,
  onStartEdit,
  onCommit,
  onCancel,
  align = 'left',
  underlineColor = 'var(--color-blue-80)',
  inputWidth = '100%',
  displaySx,
  automationId,
  autoOpen = false,
  panelAction,
}: InlineDateCellProps) => {
  if (!editing) {
    return (
      <Box
        component="span"
        data-automation-id={automationId}
        onClick={(event) => {
          event.stopPropagation();
          onStartEdit();
        }}
        sx={{
          cursor: 'pointer',
          px: 0.5,
          mx: -0.5,
          py: 0.5,
          borderRadius: 'var(--border-radius-small)',
          '&:hover': { bgcolor: 'var(--bg-hover)' },
          ...displaySx,
        }}
      >
        {display}
      </Box>
    );
  }

  return (
    <DateEditor
      value={value}
      onCommit={onCommit}
      onCancel={onCancel}
      align={align}
      underlineColor={underlineColor}
      inputWidth={inputWidth}
      automationId={automationId}
      autoOpen={autoOpen}
      panelAction={panelAction}
    />
  );
};
